import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/purchase-orders"
)({
  component: PurchaseOrdersPage,
});

import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { LocalSearchSelect } from "@/components/reusable-components/local-search-select";
import { VendorSearchSelect } from "@/components/inventory/shared/VendorSearchSelect";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchPurchaseOrders,
  usePurchaseOrderMutations,
  type PurchaseOrderOrdering,
} from "@/hooks/api/inventory/useStockOps";
import {
  ALL_ACTIVE_PARAMS,
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { PurchaseOrderTable } from "@/components/inventory/tables/PurchaseOrderTable";
import { PurchaseOrderFormSheet } from "@/components/inventory/forms/PurchaseOrderFormSheet";
import { ReceiveGoodsModal } from "@/components/inventory/forms/ReceiveGoodsModal";
import { PODetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  PurchaseOrder,
  PurchaseOrderPayload,
  PurchaseOrderStatus,
  ReceiveGoodsPayload,
} from "@/types/inventory";

const PO_STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Ordered", value: "ORDERED" },
  { label: "Partially Received", value: "PARTIALLY_RECEIVED" },
  { label: "Received", value: "RECEIVED" },
  { label: "Cancelled", value: "CANCELLED" },
] satisfies { label: string; value: PurchaseOrderStatus }[];

function PurchaseOrdersPage() {
  const navigate = useNavigate();

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    PurchaseOrderStatus | undefined
  >(undefined);
  const [vendorFilter, setVendorFilter] = useState<string | undefined>(
    undefined
  );
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<PurchaseOrder | null>(null);
  const [viewTarget, setViewTarget] = useState<PurchaseOrder | null>(null);
  const [receiveTarget, setReceiveTarget] = useState<PurchaseOrder | null>(
    null
  );
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        po_number: SortDirection;
        status: SortDirection;
        order_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      PurchaseOrderOrdering
    >({
      po_number: null,
      status: null,
      order_date: null,
      created_at: null,
      updated_at: null,
    });

  const purchaseOrdersQuery = useFetchPurchaseOrders({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    status: statusFilter,
    vendor: vendorFilter ? Number(vendorFilter) : undefined,
    warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
  });
  const purchaseOrders = purchaseOrdersQuery.data?.results ?? [];
  const count = purchaseOrdersQuery.data?.count ?? 0;

  const {
    createPurchaseOrder,
    updatePurchaseOrder,
    receiveGoods,
    cancelPurchaseOrder,
  } = usePurchaseOrderMutations();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const isMutating =
    createPurchaseOrder.isPending ||
    updatePurchaseOrder.isPending ||
    receiveGoods.isPending ||
    cancelPurchaseOrder.isPending;
  const isLoading = purchaseOrdersQuery.isPending || isMutating;
  const isFetching = purchaseOrdersQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    vendorFilter,
    warehouseFilter,
    setPage,
  ]);

  const resetFilters = () => {
    setOrdering({
      po_number: null,
      status: null,
      order_date: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setVendorFilter(undefined);
    setWarehouseFilter(undefined);
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  const handleSubmit = async (data: PurchaseOrderPayload) => {
    if (editing) {
      await updatePurchaseOrder.mutateAsync({ id: editing.id, payload: data });
      toast.success("Purchase order updated");
    } else {
      await createPurchaseOrder.mutateAsync(data);
      toast.success("Purchase order created");
    }
    setIsSheetOpen(false);
  };

  const handleReceive = (po: PurchaseOrder) => {
    setReceiveTarget(po);
  };

  const handleReceiveConfirm = async (payload: ReceiveGoodsPayload) => {
    if (!receiveTarget) return;
    await receiveGoods.mutateAsync({ id: receiveTarget.id, payload });
    toast.success("Goods received");
    setReceiveTarget(null);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelPurchaseOrder.mutateAsync({ id: cancelTarget.id });
      toast.success("Purchase order cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Purchase Orders"
          description="Manage procurement orders from vendors"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => {
            setEditing(null);
            setIsSheetOpen(true);
          }}
          addLabel="New PO"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search PO number..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={PO_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <VendorSearchSelect
            className="w-fit"
            value={vendorFilter ?? null}
            onChange={(v) => setVendorFilter(v == null ? undefined : String(v))}
            placeholder="Vendor"
          />
          <LocalSearchSelect
            className="w-fit"
            items={warehouses}
            keyExtractor={(w) => w.id}
            labelExtractor={(w) => w.name}
            watchedValue={warehouseFilter ?? null}
            onSelect={(v) => setWarehouseFilter(v == null ? undefined : String(v))}
            placeholder="Warehouse"
            searchPlaceholder="Search warehouses..."
            noItemsMessage="No warehouses found."
          />
        </FiltersPanel>

        <PurchaseOrderTable
          purchaseOrders={purchaseOrders}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(po) =>
            navigate({
              to: "/admin/inventory/purchase-orders/$id",
              params: { id: String(po.id) },
            })
          }
          onView={(po) => setViewTarget(po)}
          onEdit={(po) => {
            setEditing(po);
            setIsSheetOpen(true);
          }}
          onReceive={handleReceive}
          onCancel={(po) => setCancelTarget(po)}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <PurchaseOrderFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
          editing={editing}
        />

        {receiveTarget && (
          <ReceiveGoodsModal
            isOpen={!!receiveTarget}
            onClose={() => setReceiveTarget(null)}
            onSubmit={handleReceiveConfirm}
            purchaseOrder={receiveTarget}
            isLoading={isMutating}
          />
        )}

        {viewTarget && (
          <PODetailSheet
            isOpen={!!viewTarget}
            onClose={() => setViewTarget(null)}
            po={viewTarget}
          />
        )}

        <DeleteConfirmDialog
          isOpen={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancelConfirm}
          title="Cancel Purchase Order"
          description={`Cancel PO #${cancelTarget?.po_number}? This action cannot be undone.`}
          itemName={cancelTarget?.po_number}
          isLoading={isMutating}
          confirmLabel="Cancel Order"
          loadingLabel="Cancelling..."
          destructive
        />
      </div>
    </SidebarInset>
  );
}
